import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  WRITE_ROLES,
  type ComponentSuggestion,
  type Severity,
  type SeverityCounts,
  type SurfaceComponent,
  type SurfaceEndpoint,
  type SurfaceFinding,
  type SurfaceMap,
} from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import {
  COMPONENT_KIND_LABEL,
  draftFromSuggestion,
  type ComponentDraft,
} from '../../lib/components';
import {
  componentNodeId,
  edgePath,
  layoutSurface,
  NODE_HEIGHT,
  NODE_WIDTH,
  type LayoutNode,
} from '../../lib/surface-layout';
import { useAuth } from '../auth/auth';
import { ApplicationDialog } from './ApplicationDialog';
import { CloudView } from './CloudView';
import { ProofPathsView } from './ProofPathsView';

const SEVERITIES: Severity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'];

const date = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const totalOpen = (counts: SeverityCounts) => SEVERITIES.reduce((n, s) => n + counts[s], 0);

/** "2 high · 3 medium", the worst first; informational left out of the badge. */
function SeverityBadges({ counts }: { counts: SeverityCounts }) {
  const shown = SEVERITIES.filter((s) => s !== 'INFORMATIONAL' && counts[s] > 0);
  if (shown.length === 0) return null;
  return (
    <span className="sev-badges">
      {shown.map((s) => (
        <span key={s} className={`sev-badge sev-${s.toLowerCase()}`}>
          {counts[s]} {s.toLowerCase()}
        </span>
      ))}
    </span>
  );
}

function changeCounts(endpoints: readonly SurfaceEndpoint[]) {
  return {
    added: endpoints.filter((e) => e.change === 'NEW').length,
    gone: endpoints.filter((e) => e.change === 'GONE').length,
  };
}

/**
 * The surface map: what scans observed of an application, laid over the components it was
 * declared to have.
 *
 * Solid lines and filled boxes are observed — a scan saw them. Dashed lines are declared — a
 * person said so. The map puts the two side by side and says where they disagree: surface no
 * component accounts for, and components no scan has reached.
 */
export function SurfaceMapPage() {
  const { user } = useAuth();
  const mayWrite = user ? WRITE_ROLES.has(user.role) : false;
  const [params, setParams] = useSearchParams();

  const applications = useQuery({ queryKey: ['applications'], queryFn: api.listApplications });
  const apps = [...(applications.data?.items ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const appId = params.get('app') ?? apps[0]?.id ?? '';

  const surface = useQuery({
    queryKey: ['surface', appId],
    queryFn: () => api.getSurfaceMap(appId),
    enabled: appId.length > 0,
    // Scans finish in the background; the map follows without a reload.
    refetchInterval: 60_000,
  });

  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState<ComponentDraft | null>(null);
  /*
   * Outside-in (what scans observed), inside-out (what the cloud account holds), or the two
   * joined: each finding traced into the account (ADR 0009).
   */
  const requested = params.get('view');
  const view = requested === 'cloud' || requested === 'paths' ? requested : 'scans';
  const application = apps.find((a) => a.id === appId);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <span className="eyebrow">Workspace</span>
          <h1>Surface map</h1>
        </div>
        {apps.length > 0 ? (
          <div className="field surface-picker">
            <label htmlFor="surface-app">Application</label>
            <select
              id="surface-app"
              className="input"
              value={appId}
              onChange={(e) => {
                setSelected(null);
                setParams({ app: e.target.value, ...(view !== 'scans' ? { view } : {}) });
              }}
            >
              {apps.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {applications.isLoading ? (
        <p className="muted">Loading applications…</p>
      ) : applications.isError ? (
        <p className="login-error" role="alert">
          Could not load applications.
        </p>
      ) : apps.length === 0 ? (
        <div className="card empty">
          <p>No applications yet.</p>
          <p className="muted">
            Register an application and describe its components to start a map.
          </p>
          <Link className="btn btn-primary" to="/app/applications">
            Go to Applications
          </Link>
        </div>
      ) : null}

      {apps.length > 0 ? (
        <div className="filters view-tabs" role="tablist" aria-label="Map view">
          {(['scans', 'cloud', 'paths'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              className="filter"
              aria-selected={view === v}
              aria-pressed={view === v}
              onClick={() => setParams({ app: appId, ...(v !== 'scans' ? { view: v } : {}) })}
            >
              {v === 'scans'
                ? 'Observed by scans'
                : v === 'cloud'
                  ? 'Cloud architecture'
                  : 'Proof paths'}
            </button>
          ))}
        </div>
      ) : null}

      {apps.length === 0 ? null : view === 'paths' ? (
        application ? (
          <ProofPathsView application={application} />
        ) : null
      ) : view === 'cloud' ? (
        application ? (
          <CloudView application={application} mayWrite={mayWrite} />
        ) : null
      ) : surface.isLoading ? (
        <p className="muted">Building the map…</p>
      ) : surface.isError || !surface.data ? (
        <p className="login-error" role="alert">
          {surface.error instanceof ApiError
            ? surface.error.message
            : 'Could not load the surface map.'}
        </p>
      ) : (
        <SurfaceView
          map={surface.data}
          selected={selected}
          onSelect={setSelected}
          mayWrite={mayWrite}
          onAddSuggestion={(s) =>
            setAdding(
              draftFromSuggestion(
                s,
                surface.data.application.components.map((c) => c.name),
              ),
            )
          }
        />
      )}

      {adding && surface.data ? (
        <ApplicationDialog
          application={surface.data.application}
          extraComponent={adding}
          onClose={() => setAdding(null)}
          onSaved={() => setAdding(null)}
        />
      ) : null}
    </div>
  );
}

function SurfaceView({
  map,
  selected,
  onSelect,
  mayWrite,
  onAddSuggestion,
}: {
  map: SurfaceMap;
  selected: string | null;
  onSelect: (id: string) => void;
  mayWrite: boolean;
  onAddSuggestion: (s: ComponentSuggestion) => void;
}) {
  const layout = useMemo(() => layoutSurface(map), [map]);
  const allEndpoints = [...map.components.flatMap((c) => c.endpoints), ...map.unmapped.endpoints];
  const current = allEndpoints.filter((e) => e.change !== 'GONE');
  const openFindings =
    map.components.reduce((n, c) => n + totalOpen(c.open), 0) + totalOpen(map.unmapped.open);
  const { added } = changeCounts(allEndpoints);
  const unreached = map.components.filter(
    (c) =>
      c.endpoints.length === 0 &&
      (c.component.hosts.length > 0 || c.component.pathPrefixes.length > 0),
  );
  const alertOnly = map.scans.filter((s) => !s.crawlCaptured).length;
  const latest = map.scans[0];

  const focus =
    selected && layout.nodes.some((n) => n.id === selected)
      ? selected
      : map.unmapped.endpoints.length > 0
        ? 'unmapped'
        : map.components[0]
          ? componentNodeId(map.components[0].component.id)
          : 'internet';

  return (
    <>
      <div className="kpis surface-kpis">
        <div className="kpi">
          <span>Components declared</span>
          <span>{map.components.length}</span>
        </div>
        <div className="kpi">
          <span>Endpoints observed</span>
          <span>{current.length}</span>
        </div>
        <div className="kpi">
          <span>Open findings</span>
          <span>{openFindings}</span>
        </div>
        <div className="kpi">
          <span>Unmapped endpoints</span>
          <span>{map.unmapped.endpoints.filter((e) => e.change !== 'GONE').length}</span>
        </div>
      </div>

      <p className="muted surface-basis" role="status">
        {latest ? (
          <>
            Built from {map.scans.length} {map.scans.length === 1 ? 'scan' : 'scans'}, latest{' '}
            {date(latest.observedAt)}.
            {added > 0 ? ` ${added} endpoints are new in the latest scan.` : ''}
            {alertOnly > 0
              ? ` ${alertOnly} ${alertOnly === 1 ? 'scan' : 'scans'} recorded only where alerts were raised, not the whole crawl.`
              : ''}
            {map.scans.some((s) => s.truncated)
              ? ' Some scans saw more endpoints than are kept.'
              : ''}
          </>
        ) : (
          <>
            No scan of this application has finished yet. What is drawn is declared, not observed —
            run a passive scan from one of its engagements to fill it in.
          </>
        )}
      </p>

      {unreached.length > 0 && map.scans.length > 0 ? (
        <p className="demo-notice" role="status">
          Not reached by any scan: {unreached.map((c) => c.component.name).join(', ')}. Either the
          scope did not include them, or they are not where the components say they are.
        </p>
      ) : null}

      <section className="card panel">
        <div className="panel-head">
          <h2 className="panel-title">Architecture</h2>
          <span className="surface-legend">
            <span className="legend-line observed" /> Observed by a scan
            <span className="legend-line declared" /> Declared
          </span>
        </div>
        <div className="surface-scroll">
          <div className="surface-canvas" style={{ width: layout.width, height: layout.height }}>
            <svg
              className="surface-edges"
              width={layout.width}
              height={layout.height}
              aria-hidden="true"
            >
              {layout.edges.map((edge) => {
                const from = layout.nodes.find((n) => n.id === edge.from);
                const to = layout.nodes.find((n) => n.id === edge.to);
                if (!from || !to) return null;
                const lit = focus === edge.from || focus === edge.to;
                return (
                  <path
                    key={`${edge.from}->${edge.to}`}
                    d={edgePath(from, to)}
                    className={`edge edge-${edge.style}${lit ? ' is-lit' : ''}`}
                  />
                );
              })}
            </svg>
            {layout.nodes.map((node) => (
              <DiagramNode
                key={node.id}
                node={node}
                map={map}
                selected={focus === node.id}
                onSelect={() => onSelect(node.id)}
              />
            ))}
          </div>
        </div>
      </section>

      <NodeDetail map={map} nodeId={focus} mayWrite={mayWrite} onAddSuggestion={onAddSuggestion} />
    </>
  );
}

interface NodeView {
  title: string;
  kind: string;
  detail: string;
  counts: SeverityCounts | null;
  change: { added: number; gone: number };
  tone: string;
}

/** What a diagram box says, by the kind of node it is. */
function describeNode(node: LayoutNode, map: SurfaceMap): NodeView {
  const none = { added: 0, gone: 0 };
  if (node.kind === 'internet') {
    return {
      title: 'Internet',
      kind: 'Where a scan starts',
      detail:
        map.externalHosts.length > 0
          ? `${map.externalHosts.length} external hosts referenced`
          : 'Outside the application',
      counts: null,
      change: none,
      tone: '',
    };
  }
  if (node.kind === 'host') {
    const name = node.id.slice('host:'.length);
    const host = map.hosts.find((h) => h.host === name);
    return {
      title: name,
      kind: 'Host',
      detail: host ? `${host.endpointCount} endpoints observed` : 'Declared, not yet observed',
      counts: null,
      change: none,
      tone: host ? '' : 'is-declared',
    };
  }
  if (node.kind === 'unmapped') {
    const live = map.unmapped.endpoints.filter((e) => e.change !== 'GONE').length;
    return {
      title: 'Unmapped surface',
      kind: 'No component accounts for it',
      detail: `${live} endpoints`,
      counts: map.unmapped.open,
      change: changeCounts(map.unmapped.endpoints),
      tone: 'is-warning',
    };
  }
  const c = map.components.find((x) => componentNodeId(x.component.id) === node.id)!;
  const live = c.endpoints.filter((e) => e.change !== 'GONE').length;
  const reachable = c.component.hosts.length > 0 || c.component.pathPrefixes.length > 0;
  return {
    title: c.component.name,
    kind: COMPONENT_KIND_LABEL[c.component.kind],
    detail:
      c.endpoints.length > 0
        ? `${live} endpoints${c.observedHosts.length > 1 ? ` on ${c.observedHosts.length} hosts` : ''}`
        : reachable
          ? 'Not reached by a scan yet'
          : 'Declared · not reachable by a scan',
    counts: c.open,
    change: changeCounts(c.endpoints),
    tone: c.endpoints.length > 0 ? '' : 'is-declared',
  };
}

function DiagramNode({
  node,
  map,
  selected,
  onSelect,
}: {
  node: LayoutNode;
  map: SurfaceMap;
  selected: boolean;
  onSelect: () => void;
}) {
  const { title, kind, detail, counts, change, tone } = describeNode(node, map);

  return (
    <button
      type="button"
      className={`surface-node node-${node.kind} ${tone}${selected ? ' is-selected' : ''}`}
      style={{ left: node.x, top: node.y, width: NODE_WIDTH, height: NODE_HEIGHT }}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span className="node-kind">{kind}</span>
      <span className={`node-title${node.kind === 'host' ? ' mono' : ''}`}>{title}</span>
      <span className="node-detail">
        {detail}
        {change.added > 0 ? <span className="change-new">+{change.added} new</span> : null}
        {change.gone > 0 ? <span className="change-gone">−{change.gone} gone</span> : null}
      </span>
      {counts ? <SeverityBadges counts={counts} /> : null}
    </button>
  );
}

function NodeDetail({
  map,
  nodeId,
  mayWrite,
  onAddSuggestion,
}: {
  map: SurfaceMap;
  nodeId: string;
  mayWrite: boolean;
  onAddSuggestion: (s: ComponentSuggestion) => void;
}) {
  if (nodeId === 'internet') {
    return (
      <section className="card panel">
        <h2 className="panel-title">External hosts</h2>
        <p className="muted" style={{ fontSize: 13 }}>
          Hosts the crawl saw referenced that are not in any scan's approved scope. They were never
          scanned; they show what the application depends on from outside.
        </p>
        {map.externalHosts.length === 0 ? (
          <p className="muted">None referenced.</p>
        ) : (
          <ul className="surface-list mono">
            {map.externalHosts.map((h) => (
              <li key={h.host}>
                {h.host} <span className="muted">· {h.references} references</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }

  if (nodeId.startsWith('host:')) {
    const name = nodeId.slice('host:'.length);
    const endpoints = [...map.components.flatMap((c) => c.endpoints), ...map.unmapped.endpoints]
      .filter((e) => e.host === name)
      .sort((a, b) => a.path.localeCompare(b.path));
    const declaredBy = map.components.filter((c) => c.component.hosts.includes(name));
    return (
      <section className="card panel">
        <h2 className="panel-title mono">{name}</h2>
        <p className="muted" style={{ fontSize: 13 }}>
          {declaredBy.length > 0
            ? `Declared by ${declaredBy.map((c) => c.component.name).join(', ')}.`
            : 'No component declares this host.'}
        </p>
        <EndpointTable endpoints={endpoints} map={map} showComponent />
      </section>
    );
  }

  if (nodeId === 'unmapped') {
    return (
      <section className="card panel">
        <h2 className="panel-title">Unmapped surface</h2>
        <p className="muted" style={{ fontSize: 13 }}>
          Observed, but no declared component accounts for it — so nobody has said what it is or who
          owns it. Either add a component for it or correct an existing one's hosts and paths.
        </p>
        {map.suggestions.length > 0 ? (
          <>
            <h3 className="detail-title">Suggested components</h3>
            <p className="field-hint">
              Grouped by host and first path segment. A suggestion is only added when you save it.
            </p>
            <ul className="suggestions">
              {map.suggestions.map((s) => (
                <li key={`${s.host}${s.pathPrefix ?? ''}`}>
                  <span>
                    <strong>{s.name}</strong>{' '}
                    <span className="muted">
                      · {COMPONENT_KIND_LABEL[s.kind]} ·{' '}
                      <span className="mono">
                        {s.host}
                        {s.pathPrefix ?? ''}
                      </span>{' '}
                      · {s.endpointCount} endpoints
                    </span>
                  </span>
                  {mayWrite ? (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => onAddSuggestion(s)}
                    >
                      Add as component
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}
        <FindingList findings={map.unmapped.findings} />
        <EndpointTable endpoints={map.unmapped.endpoints} map={map} />
      </section>
    );
  }

  const c = map.components.find((x) => componentNodeId(x.component.id) === nodeId);
  if (!c) return null;
  return <ComponentDetail c={c} map={map} />;
}

function ComponentDetail({ c, map }: { c: SurfaceComponent; map: SurfaceMap }) {
  const name = (id: string) =>
    map.components.find((x) => x.component.id === id)?.component.name ?? id;
  const callers = map.components.filter((x) => x.component.connectsTo.includes(c.component.id));
  return (
    <section className="card panel">
      <h2 className="panel-title">
        {c.component.name}{' '}
        <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>
          · {COMPONENT_KIND_LABEL[c.component.kind]}
        </span>
      </h2>
      {c.component.description ? <p>{c.component.description}</p> : null}
      <dl className="kv">
        <dt>Declared hosts</dt>
        <dd className="mono">{c.component.hosts.join(', ') || 'Any'}</dd>
        <dt>Path prefixes</dt>
        <dd className="mono">{c.component.pathPrefixes.join(', ') || 'Whole host'}</dd>
        <dt>Observed on</dt>
        <dd className="mono">{c.observedHosts.join(', ') || '—'}</dd>
        <dt>Connects to</dt>
        <dd>{c.component.connectsTo.map(name).join(', ') || '—'}</dd>
        <dt>Called by</dt>
        <dd>{callers.map((x) => x.component.name).join(', ') || '—'}</dd>
      </dl>
      {c.component.hosts.length === 0 && c.component.pathPrefixes.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>
          This component declares no host or path, so nothing a scan observes is placed on it. It
          appears on the map through the connections declared to it.
        </p>
      ) : null}
      <FindingList findings={c.findings} />
      <EndpointTable endpoints={c.endpoints} map={map} />
    </section>
  );
}

function FindingList({ findings }: { findings: readonly SurfaceFinding[] }) {
  if (findings.length === 0) return null;
  return (
    <>
      <h3 className="detail-title">Open findings ({findings.length})</h3>
      <ul className="surface-findings">
        {findings.map((f) => (
          <li key={f.id}>
            <span className={`sev-${f.severity.toLowerCase()}`}>{f.severity}</span>{' '}
            <Link className="table-link" to={`/app/engagements/${f.engagementId}`}>
              {f.title}
            </Link>{' '}
            <span className="muted mono">{f.path ?? ''}</span>{' '}
            <span className={`finding-status fs-${f.status.toLowerCase()}`}>
              {f.status.replace('_', ' ')}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

const PAGE = 100;

function EndpointTable({
  endpoints,
  map,
  showComponent = false,
}: {
  endpoints: readonly SurfaceEndpoint[];
  map: SurfaceMap;
  showComponent?: boolean;
}) {
  const [filter, setFilter] = useState('');
  const [limit, setLimit] = useState(PAGE);
  if (endpoints.length === 0) {
    return <p className="muted">No endpoints observed.</p>;
  }
  const needle = filter.trim().toLowerCase();
  const rows = needle
    ? endpoints.filter((e) => `${e.host}${e.path}`.toLowerCase().includes(needle))
    : endpoints;
  const owner = (e: SurfaceEndpoint) =>
    map.components.find((c) => c.endpoints.includes(e))?.component.name ?? 'Unmapped';

  return (
    <>
      <div className="panel-head" style={{ marginTop: 16 }}>
        <h3 className="detail-title">Endpoints ({endpoints.length})</h3>
        <input
          className="input endpoint-filter"
          aria-label="Filter endpoints"
          placeholder="Filter by path"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>
      <div className="table-wrap">
        <table className="table endpoint-table">
          <thead>
            <tr>
              <th>Path</th>
              {showComponent ? <th>Component</th> : null}
              <th>Kind</th>
              <th>Methods</th>
              <th>Status</th>
              <th>Inputs</th>
              <th>Findings</th>
              <th>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, limit).map((e) => (
              <tr
                key={`${e.host}${e.path}`}
                className={e.change === 'GONE' ? 'is-gone' : undefined}
              >
                <td className="mono endpoint-path">
                  {e.path}
                  {e.change === 'NEW' ? <span className="change-new">new</span> : null}
                  {e.change === 'GONE' ? <span className="change-gone">gone</span> : null}
                </td>
                {showComponent ? <td>{owner(e)}</td> : null}
                <td>{e.kind.toLowerCase()}</td>
                <td className="mono">{e.methods.join(' ') || '—'}</td>
                <td className="mono">{e.statuses.join(' ') || '—'}</td>
                <td className="mono">{e.params.join(', ') || '—'}</td>
                <td>{e.findingIds.length || '—'}</td>
                <td className="muted">{date(e.lastSeenAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > limit ? (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setLimit(limit + PAGE)}
        >
          Show more ({rows.length - limit} left)
        </button>
      ) : null}
    </>
  );
}
